import React, { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { useGetRepositoryByIdQuery } from '../services/repositoryEntries';
import { EntityType, type Repository } from '../types';
import ChartWhereFilterBuilder from './ChartWhereFilterBuilder';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectAllPropertyDefinitions } from '../containers/contentSlice';
import { sortEntryByRank } from '../common/util';

export interface AddQueryDialogProps {
	open: boolean;
	repositories: Repository[];
	onClose: () => void;
	onSubmit: (payload: { repositoryId: string; title: string; where?: string; includeArchived: boolean }) => void;
	elementSelectSlot?: React.ReactNode;
}

const AddQueryDialog: React.FC<AddQueryDialogProps> = ({ open, repositories, onClose, onSubmit, elementSelectSlot }) => {
	const [repositoryId, setRepositoryId] = useState('');
	const [title, setTitle] = useState('');
	const [where, setWhere] = useState('');
	const [includeArchived, setIncludeArchived] = useState(true);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);

	const { data: repositoryById, isFetching: isRepositoryFetching } = useGetRepositoryByIdQuery(
		repositoryId,
		{ skip: !open || !repositoryId || !isAuthenticated }
	);
	const localPropertyDefinitions = useMemo(
		() =>
			allPropertyDefinitions.filter((def) =>
				def.parentId === repositoryId
			).sort(sortEntryByRank),
		[allPropertyDefinitions, repositoryId]
	);
	const propertyDefinitionsSource = useMemo(
		() =>
			(isAuthenticated ? (repositoryById?.propertyDefinitions ?? []) : localPropertyDefinitions) as Array<{
				slug: string;
				label?: string;
				type: EntityType;
				required?: boolean;
				targetRepositoryId?: string;
			}>,
		[isAuthenticated, repositoryById?.propertyDefinitions, localPropertyDefinitions]
	);
	const propertyOptions = useMemo(
		() =>
			propertyDefinitionsSource
				.map((property) => ({
					slug: property.slug,
					label: property.label?.trim() ? property.label : property.slug,
					type: property.type,
					required: (property as { required?: boolean }).required === true,
					targetRepositoryId:
						property.type === EntityType.PropertyDefinitionBelongsTo
							? String(
								(property as { targetRepositoryId?: string }).targetRepositoryId ?? '',
							).trim()
							: '',
				})),
		[propertyDefinitionsSource]
	);
	useEffect(() => {
		if (!open) {
			return;
		}
		setRepositoryId(repositories[0]?.id ?? '');
		setTitle('');
		setWhere('');
		setIncludeArchived(true);
	}, [open, repositories]);

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		const trimmed = title.trim();
		const normalizedWhere = where.trim();
		if (!repositoryId || !trimmed) {
			return;
		}
		onSubmit({
			repositoryId,
			title: trimmed,
			where: normalizedWhere || undefined,
			includeArchived,
		});
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="body">
			<form onSubmit={handleSubmit} autoComplete="off">
				{elementSelectSlot != null ? (
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>Add query</DialogTitle>
						<Box pr={2}>{elementSelectSlot}</Box>
					</Box>
				) : (
					<DialogTitle>Add query</DialogTitle>
				)}
				<Container>
					<Typography variant="body2" sx={{color: 'text.secondary'}}>
						Use queries to access key insights at a glance.
					</Typography>
				</Container>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={elementSelectSlot != null ? 0 : 1}>
						<TextField
							label="Title"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							fullWidth
							required
							autoFocus
							size="small"
							placeholder="Name this query"
						/>
						<FormControl fullWidth required size="small">
							<InputLabel id="add-query-repository-label">Collection</InputLabel>
							<Select
								labelId="add-query-repository-label"
								label="Collection"
								value={repositoryId}
								onChange={(e) => {
									setRepositoryId(String(e.target.value));
									setWhere('');
									setIncludeArchived(true);
								}}
							>
								{repositories.map((r) => (
									<MenuItem key={r.id} value={r.id}>
										{r.title}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{open && (
							<ChartWhereFilterBuilder
								chartId={repositoryId || '__none__'}
								initialWhere={where}
								onChange={setWhere}
								properties={propertyOptions}
								disabled={!repositoryId || isRepositoryFetching}
								includeArchived={includeArchived}
								onIncludeArchivedChange={setIncludeArchived}
								shouldAskAboutRelativeTime={true}
							/>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button type="button" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={!repositoryId || !title.trim()}>
						Add
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default AddQueryDialog;
